import { StoryBoard } from 'services/storyboard.service';
import { Error } from 'components/app/_helpers/app-errors.helper';
import { findSbPosition, getSbTracks, sortClips } from 'components/app/_helpers/_app.helper';
import {
  Clip,
  ClipsMap,
  EMPTY_CELL,
  LinkedClips,
  Selector,
  UNLINKED_ID,
} from 'components/app/editor/_mediator/editor.slice';
import { SbPosition } from 'components/app/editor/_mediator/editor-controls.slice';
import { findLinkByClipTime } from './editor-clips-linking.helper';
const ERR_ID = 'clipsCRUD';

const api = {
  storyBoard: {
    add: (clips: Clip[], storyBoard: StoryBoard, position?: SbPosition) => {
      const newStoryBoard: Partial<StoryBoard> = {};
      const error = clips.some((clip) => {
        const sb = { ...storyBoard, ...newStoryBoard };
        const pos_1 = position ? findSbPosition(EMPTY_CELL, sb, position) : findAfterLastClipPosition(sb);
        const pos_2 = pos_1 ?? findSbPosition(EMPTY_CELL, sb);
        const pos = pos_1 ?? pos_2;
        if (!pos) return true;
        const { track, cell } = pos;
        const clips = newStoryBoard[track] || storyBoard[track];
        newStoryBoard[track] = [...clips];
        newStoryBoard[track]![cell] = clip.clipId;
      });
      if (!error) return newStoryBoard;
      console.error(`[${ERR_ID}] storyBoardAdd`);
      return {};
    },
    delete: (clips: Clip[], storyBoard: StoryBoard) => {
      const newStoryBoard: Partial<StoryBoard> = {};
      const error = clips.some((clip) => {
        const { clipId } = clip;
        const position = findSbPosition(clipId, storyBoard);
        if (!position) return true;
        const clips = newStoryBoard[position.track] || storyBoard[position.track];
        newStoryBoard[position.track] = [...clips];
        newStoryBoard[position.track]![position.cell] = EMPTY_CELL;
      });
      if (!error) return newStoryBoard;
      console.error(`[${ERR_ID}] storyBoardDel`);
      return {};
    },
  },
  linkedClips: {
    add: (_clips: Clip[], linkedClips: LinkedClips) => {
      const newLinkedClips: LinkedClips = {};
      const clips = _clips.filter((clip) => clip.clipGroupId !== UNLINKED_ID);
      const ids = new Set(clips.map((clip) => clip.clipGroupId));
      ids.forEach((id) => {
        newLinkedClips[id] = linkedClips[id] ? [...linkedClips[id]] : [];
      });
      clips.forEach((clip) => {
        const { clipGroupId, clipId } = clip;
        newLinkedClips[clipGroupId].push(clipId);
      });
      Object.values(newLinkedClips).forEach((clips) => sortClips(clips));
      return newLinkedClips;
    },
    delete: (_clips: Clip[], linkedClips: LinkedClips) => {
      const newLinkedClips: LinkedClips = {};
      const clips = _clips.filter((clip) => clip.clipGroupId !== UNLINKED_ID);
      const error = clips.some((clip) => {
        const { clipGroupId, clipId } = clip;
        const clips = newLinkedClips[clipGroupId] || linkedClips[clipGroupId];
        if (!clips) return true;
        newLinkedClips[clipGroupId] = clips.filter((el) => el !== clipId);
      });
      if (!error) return newLinkedClips;
      console.error(`[${ERR_ID}] linkedClipsDel: linked group not found`);
      return linkedClips;
    },
  },
  selector: {
    add: (clips: Clip[], selector: Selector) => {
      const newSelector: Selector = {};
      clips.forEach((clip) => {
        const { mediaFileId, clipId } = clip;
        const clips = newSelector[mediaFileId] || selector[mediaFileId];
        newSelector[mediaFileId] = clips ? sortClips([...clips, clipId]) : [clipId];
      });
      return newSelector;
    },
    delete: (clips: Clip[], selector: Selector) => {
      const newSelector: Selector = {};
      const error = clips.some((clip) => {
        const { mediaFileId, clipId } = clip;
        const clips = newSelector[mediaFileId] || selector[mediaFileId];
        if (!clips) return true;
        newSelector[mediaFileId] = clips.filter((el) => el !== clipId);
      });
      if (!error) return newSelector;
      console.error(`[${ERR_ID}] selectorDel`);
      return {};
    },
  },
  clipsMap: {
    add: (clips: Clip[]) => {
      const clipMap: ClipsMap = {};
      clips.forEach((clip) => (clipMap[clip.clipId] = clip));
      return clipMap;
    },
    delete: (clips: Clip[]) => {
      const clipMap: ClipsMap = {};
      clips.forEach((clip) => (clipMap[clip.clipId] = null));
      return clipMap;
    },
  },
};

export const clipsApi = (selector: Selector, storyBoard: StoryBoard, linkedClips: LinkedClips) => ({
  add: (clips: Clip[], position?: SbPosition) => ({
    clipsMap: api.clipsMap.add(clips),
    storyBoard: api.storyBoard.add(clips, storyBoard, position),
    linkedClips: api.linkedClips.add(clips, linkedClips),
    selector: api.selector.add(clips, selector),
  }),
  del: (clips: Clip[], partial?: boolean) => {
    const res = {
      clipsMap: api.clipsMap.delete(clips),
      storyBoard: api.storyBoard.delete(clips, storyBoard) as StoryBoard,
      linkedClips: api.linkedClips.delete(clips, linkedClips),
      selector: api.selector.delete(clips, selector),
    };
    return partial
      ? res
      : {
          clipsMap: res.clipsMap,
          storyBoard: { ...storyBoard, ...res.storyBoard },
          linkedClips: { ...linkedClips, ...res.linkedClips },
          selector: { ...selector, ...res.selector },
        };
  },
});

export const linksApi = (linkedClips: LinkedClips) => ({
  link: (_clips: Clip[], linkId: number) => {
    const clips = _clips.map((clip) => ({ ...clip, clipGroupId: linkId }));
    return {
      clipsMap: api.clipsMap.add(clips),
      linkedClips: api.linkedClips.add(clips, linkedClips),
    };
  },
  unlink: (clips: Clip[]) => {
    return {
      clipsMap: api.clipsMap.add(clips.map((clip) => ({ ...clip, clipGroupId: UNLINKED_ID }))),
      linkedClips: api.linkedClips.delete(clips, linkedClips),
    };
  },
});

export const handleNewClips = (clips: Clip[], playerTime: EditorTime, clipsMap: ClipsMap, linkedClips: LinkedClips) => {
  const groupId = findLinkByClipTime(playerTime, linkedClips, clips[0].mediaFileId);
  if (groupId) clips.forEach((clip) => (clip.clipGroupId = groupId));
  return sortClips(
    clips.filter((clip) => {
      if (!clipsMap[clip.clipId]) return clip;
      Error.clipExists(clip);
    })
  );
};

/* H E L P E R S */

const findAfterLastClipPosition = (sb: StoryBoard): SbPosition | undefined => {
  const tracks = getSbTracks();
  for (const track of tracks) {
    const cell = sb[track].findLastIndex((el) => el !== EMPTY_CELL) + 1;
    if (cell === 0) return { track, cell: 0 };
    if (cell < sb[track].length) return { track, cell };
  }
};
